export const mapDeviceToSidebarItem = (item) => {
  const info = item?.info || {};
  return {
    id: info.macAddress || info.id || "",
    name: info.deviceName || info.macAddress || "",
    siteId: info.siteId || info.siteID || info.site_id || info.site?.id || "",
    favorited: info.favorite === true,
    deviceType: info.deviceType,
    deviceParentId: info.deviceParentId || null,
    linkStatus: info.linkStatus || "offline",
  };
};

export const partitionSurveillanceDevices = (items) => {
  const mapped = items.map(mapDeviceToSidebarItem).filter((device) => device.id);
  const nvrs = mapped
    .filter((device) => device.deviceType === "nvr")
    .map((nvr) => ({ ...nvr, cameras: [] }));
  const nvrById = Object.fromEntries(nvrs.map((nvr) => [nvr.id, nvr]));
  const devices = [];

  mapped.forEach((device) => {
    if (device.deviceType === "nvr") return;
    if (device.deviceType !== "ipcam" && device.deviceType) return;
    const parentNvr = device.deviceParentId && nvrById[device.deviceParentId];
    if (parentNvr) parentNvr.cameras.push(device);
    else devices.push(device);
  });

  return { devices, nvrs };
};

/**
 * NVR re-mux / archive pipeline stamps Header PTS ~1.1s ahead of the camera
 * burn-in OSD. Display-only: never apply to Seek API or video.currentTime.
 * Measured from PlaybackDebug overlay vs OSD (fixed skew, not cumulative).
 */
export const NVR_OSD_COMPENSATION_MS = -1100;

/** True for cameras under an NVR (deviceParentId set), not Direct ipcam. */
export const isNvrSubDevice = (device) => Boolean(device?.deviceParentId);

/** Media-clock PTS → OSD-aligned display PTS (Direct unchanged). */
export const toOsdDisplayPtsMs = (mediaPtsMs, isNvrChannel) => {
  if (!Number.isFinite(mediaPtsMs)) return mediaPtsMs;
  return isNvrChannel ? mediaPtsMs + NVR_OSD_COMPENSATION_MS : mediaPtsMs;
};

/** Inverse of toOsdDisplayPtsMs — for Seek / API from a display-picked time. */
export const toMediaPtsMs = (displayPtsMs, isNvrChannel) => {
  if (!Number.isFinite(displayPtsMs)) return displayPtsMs;
  return isNvrChannel ? displayPtsMs - NVR_OSD_COMPENSATION_MS : displayPtsMs;
};

export const getPlaybackRecordList = (response) => {
  const records = response?.data?.records || response?.records || response?.data;
  return Array.isArray(records)
    ? records.filter((record) => record && typeof record === "object")
    : [];
};

export const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatLocalTime = (date, includeSeconds = false) => {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
};

export const formatLocalDateTime = (date, includeSeconds = true) =>
  `${formatLocalDate(date)} ${formatLocalTime(date, includeSeconds)}`;

export const getPlaybackEventDates = (response) => {
  const eventDates = new Set();

  getPlaybackRecordList(response).forEach((record) => {
    const startTime = Number(record?.startTime) * 1000;
    const endTime = Number(record?.endTime) * 1000;
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime < startTime) {
      return;
    }

    const currentDate = new Date(startTime);
    currentDate.setHours(0, 0, 0, 0);
    const endDate = new Date(endTime);
    endDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      eventDates.add(formatLocalDate(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
  });

  return Array.from(eventDates).sort();
};

export const formatRecordTimeRange = (startTime, endTime) => {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  const isSameDay =
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate();

  const startFormatted = formatLocalDateTime(startDate, true);
  const endFormatted = isSameDay
    ? formatLocalTime(endDate, true)
    : formatLocalDateTime(endDate, true);

  return `${startFormatted} ~ ${endFormatted}`;
};

export const getTodayStartTimestamp = () => {
  // Local midnight (browser timezone), not UTC midnight.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today.getTime();
};

export const getDayStartTimestamp = (timestamp) => {
  // Local calendar day start — matches orca portal parsePlaybackTime(local) → getTime().
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

/**
 * Day range for getPlaybackRecords / filtering.
 * Uses local 00:00:00–23:59:59.999, then converts to Unix seconds for the API
 * (same contract as orca: UI local time → Math.floor(date.getTime() / 1000)).
 */
export const getPlaybackDayRange = (timestamp) => {
  const startTime = getDayStartTimestamp(timestamp);
  const endTime = new Date(startTime);
  endTime.setHours(23, 59, 59, 999);
  return {
    startTime: Math.floor(startTime / 1000),
    endTime: Math.floor(endTime.getTime() / 1000),
  };
};

export const getPlaybackMonthRange = (timestamp) => {
  const date = new Date(timestamp);
  const startTime = new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
  const endTime = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
  return {
    startTime: Math.floor(startTime.getTime() / 1000),
    endTime: Math.floor(endTime.getTime() / 1000),
  };
};

export const getInitialPlaybackTime = (response, selectedDate, isMultiChannel) => {
  const dayStart = getDayStartTimestamp(selectedDate);
  if (isMultiChannel) return dayStart;
  const dayRange = getPlaybackDayRange(selectedDate);

  const firstStartTime = getPlaybackRecordList(response).reduce((earliest, record) => {
    const startTime = Number(record?.startTime) * 1000;
    return Number.isFinite(startTime) &&
      startTime >= dayRange.startTime * 1000 &&
      startTime <= dayRange.endTime * 1000
      ? Math.min(earliest, startTime)
      : earliest;
  }, Infinity);

  return Number.isFinite(firstStartTime) ? firstStartTime : dayStart;
};

/**
 * Find the recording segment that **covers** `timeMs` (inclusive start, exclusive end).
 * Returns null for timeline gaps — callers must not snap to another segment on seek.
 * Times in the return value are Unix seconds for createPlaybackStream.
 */
export const findCoveringRecordAtTime = (response, timeMs) => {
  const dayRange = getPlaybackDayRange(timeMs);
  const dayStartMs = dayRange.startTime * 1000;
  const dayEndMs = dayRange.endTime * 1000;

  const records = getPlaybackRecordList(response)
    .map((record) => ({
      startMs: Number(record?.startTime) * 1000,
      endMs: Number(record?.endTime) * 1000,
    }))
    .filter(
      (record) =>
        Number.isFinite(record.startMs) &&
        Number.isFinite(record.endMs) &&
        record.endMs > record.startMs &&
        record.endMs >= dayStartMs &&
        record.startMs <= dayEndMs
    )
    .sort((a, b) => a.startMs - b.startMs);

  const covering = records.find(
    (record) => timeMs >= record.startMs && timeMs < record.endMs
  );
  if (!covering) return null;

  const seekMs = Math.max(
    covering.startMs,
    Math.min(timeMs, covering.endMs - 1000)
  );
  return {
    startTime: Math.floor(seekMs / 1000),
    endTime: Math.floor(covering.endMs / 1000),
    recordStartMs: covering.startMs,
    recordEndMs: covering.endMs,
  };
};

/**
 * For auto-start when entering a camera/day: covering segment, else the next
 * upcoming segment that day (so empty leading gap still starts at first event).
 */
export const findPlayableRecordAtTime = (response, timeMs) => {
  const covering = findCoveringRecordAtTime(response, timeMs);
  if (covering) return covering;

  const dayRange = getPlaybackDayRange(timeMs);
  const dayStartMs = dayRange.startTime * 1000;
  const dayEndMs = dayRange.endTime * 1000;

  const upcoming = getPlaybackRecordList(response)
    .map((record) => ({
      startMs: Number(record?.startTime) * 1000,
      endMs: Number(record?.endTime) * 1000,
    }))
    .filter(
      (record) =>
        Number.isFinite(record.startMs) &&
        Number.isFinite(record.endMs) &&
        record.endMs > record.startMs &&
        record.endMs >= dayStartMs &&
        record.startMs <= dayEndMs &&
        record.startMs >= timeMs
    )
    .sort((a, b) => a.startMs - b.startMs)[0];

  if (!upcoming) return null;

  return {
    startTime: Math.floor(upcoming.startMs / 1000),
    endTime: Math.floor(upcoming.endMs / 1000),
    recordStartMs: upcoming.startMs,
    recordEndMs: upcoming.endMs,
  };
};

export const TIMELINE_SCALE_CONFIG = {
  "10M": { totalMinutes: 10, majorMinutes: 1, minorSeconds: 15 },
  "1H": { totalMinutes: 60, majorMinutes: 10, minorSeconds: 60 },
  "6H": { totalMinutes: 360, majorMinutes: 60, minorSeconds: 600 },
  "12H": { totalMinutes: 720, majorMinutes: 120, minorSeconds: 600 },
  "24H": { totalMinutes: 1440, majorMinutes: 240, minorSeconds: 1800 },
};

export const SPEED_OPTIONS = [
  "1/16X",
  "1/8X",
  "1/4X",
  "1/2X",
  "1X",
  "2X",
  "4X",
  "8X",
  "16X",
];
